import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, RefreshCw, Trash2, AlertTriangle, CheckCircle2, Sparkles, Search, Send, X as XIcon } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BotLinksAdmin() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [revoking, setRevoking] = useState(null); // chat_id
  const [query, setQuery] = useState('');
  const [staleOnly, setStaleOnly] = useState(false);
  const [composeTarget, setComposeTarget] = useState(null); // link row being composed to
  const [composeText, setComposeText] = useState('');
  const [sending, setSending] = useState(false);
  const [sentToast, setSentToast] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('wclub_admin_token');
      if (!token) { setError('Login como admin necessário.'); setLoading(false); return; }
      const res = await axios.get(`${API}/admin/bot-links`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Erro ao carregar vínculos.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const revoke = async (link) => {
    const who = link.member_name || link.telegram_username || link.phone || link.chat_id;
    if (!window.confirm(`Revogar vínculo de ${who}?\n\nO usuário será desconectado do bot e precisará compartilhar o contato de novo.`)) return;
    setRevoking(link.chat_id);
    try {
      const token = localStorage.getItem('wclub_admin_token');
      await axios.delete(`${API}/admin/bot-links/${link.chat_id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Erro ao revogar vínculo.');
    } finally {
      setRevoking(null);
    }
  };

  const openCompose = (link) => {
    setComposeTarget(link);
    setComposeText('');
    setError(null);
  };

  const closeCompose = () => {
    if (sending) return;
    setComposeTarget(null);
    setComposeText('');
  };

  const sendMessage = async () => {
    if (!composeTarget || !composeText.trim()) return;
    setSending(true);
    setError(null);
    try {
      const token = localStorage.getItem('wclub_admin_token');
      await axios.post(
        `${API}/admin/bot-links/${composeTarget.chat_id}/send-message`,
        { text: composeText.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const who = composeTarget.member_name || composeTarget.telegram_username || composeTarget.phone;
      setSentToast(`Enviado para ${who}`);
      setTimeout(() => setSentToast(null), 3500);
      setComposeTarget(null);
      setComposeText('');
    } catch (e) {
      setError(e?.response?.data?.detail || 'Erro ao enviar mensagem.');
    } finally {
      setSending(false);
    }
  };

  const rows = (data?.links || []).filter(r => {
    if (staleOnly && !r.is_stale) return false;
    if (!query.trim()) return true;
    const q = query.trim().toLowerCase();
    return (
      String(r.chat_id).includes(q) ||
      (r.telegram_username || '').toLowerCase().includes(q) ||
      (r.phone || '').includes(q) ||
      (r.member_name || '').toLowerCase().includes(q) ||
      (r.member_id || '').toLowerCase().includes(q)
    );
  });

  const staleCount = (data?.links || []).filter(r => r.is_stale).length;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-black text-white pb-20">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button
            data-testid="bot-links-back-btn"
            onClick={() => navigate('/')}
            className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 transition"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Voltar</span>
          </button>
          <button
            data-testid="bot-links-refresh-btn"
            onClick={load}
            className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 transition"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span className="text-sm">Atualizar</span>
          </button>
        </div>

        {/* Title */}
        <div className="mb-6">
          <h1 className="text-3xl sm:text-4xl font-light tracking-tight flex items-center gap-3">
            <Sparkles className="w-7 h-7 text-cyan-400" />
            Vínculos de Bot
          </h1>
          <p className="text-sm text-white/50 mt-2">
            Todos os Telegrams (@W_ia_bot) vinculados a sócios ou planos W IA. Revogue para forçar re-autenticação.
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
          <div className="rounded-2xl bg-white/5 border border-white/10 p-4">
            <div className="text-xs text-white/50 uppercase tracking-wider">Total</div>
            <div className="text-2xl font-light mt-1" data-testid="bot-links-total">{data?.count ?? '—'}</div>
          </div>
          <div className="rounded-2xl bg-emerald-400/5 border border-emerald-400/20 p-4">
            <div className="text-xs text-emerald-300/80 uppercase tracking-wider">Ativos</div>
            <div className="text-2xl font-light mt-1 text-emerald-200">
              {(data?.links || []).filter(r => !r.is_stale).length}
            </div>
          </div>
          <div className="rounded-2xl bg-amber-400/5 border border-amber-400/20 p-4 col-span-2 sm:col-span-1">
            <div className="text-xs text-amber-300/80 uppercase tracking-wider flex items-center gap-1.5">
              <AlertTriangle className="w-3 h-3" /> Órfãos
            </div>
            <div className="text-2xl font-light mt-1 text-amber-200" data-testid="bot-links-stale-count">{staleCount}</div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="flex-1 relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <input
              data-testid="bot-links-search"
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar por username, telefone, sócio ou chat_id..."
              className="w-full pl-10 pr-3 py-2.5 rounded-xl bg-white/5 border border-white/10 focus:border-cyan-400/50 focus:outline-none text-sm placeholder-white/30"
            />
          </div>
          <label className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 cursor-pointer hover:bg-white/10 transition">
            <input
              data-testid="bot-links-stale-toggle"
              type="checkbox"
              checked={staleOnly}
              onChange={(e) => setStaleOnly(e.target.checked)}
              className="accent-amber-400"
            />
            <span className="text-sm text-white/80">Só órfãos</span>
          </label>
        </div>

        {error && (
          <div className="mb-4 p-4 rounded-xl bg-red-500/10 border border-red-500/30 text-red-200 text-sm" data-testid="bot-links-error">
            {error}
          </div>
        )}

        {/* Table */}
        <div className="rounded-2xl bg-white/[0.03] border border-white/10 overflow-hidden">
          {loading && !data ? (
            <div className="p-12 text-center text-white/40">Carregando...</div>
          ) : rows.length === 0 ? (
            <div className="p-12 text-center text-white/40" data-testid="bot-links-empty">
              {(data?.count || 0) === 0
                ? 'Nenhum vínculo registrado ainda.'
                : 'Nenhum vínculo corresponde aos filtros.'}
            </div>
          ) : (
            <div className="divide-y divide-white/5">
              {rows.map((r) => (
                <div key={r.chat_id} className="p-4 flex flex-col sm:flex-row sm:items-center gap-3 hover:bg-white/[0.02] transition" data-testid={`bot-link-row-${r.chat_id}`}>
                  {/* Left: Identity */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      {r.is_stale ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-400/10 border border-amber-400/30 text-amber-200 uppercase tracking-wider">
                          <AlertTriangle className="w-3 h-3" /> Órfão
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-emerald-400/10 border border-emerald-400/30 text-emerald-200 uppercase tracking-wider">
                          <CheckCircle2 className="w-3 h-3" /> Ativo
                        </span>
                      )}
                      {r.wia_only && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-cyan-400/10 border border-cyan-400/30 text-cyan-200 uppercase tracking-wider">
                          W IA
                        </span>
                      )}
                      {r.plan_active && r.plan_tier && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-fuchsia-400/10 border border-fuchsia-400/30 text-fuchsia-200 uppercase tracking-wider">
                          {r.plan_tier}
                        </span>
                      )}
                    </div>

                    <div className="mt-2 text-base font-medium text-white/95 truncate">
                      {r.member_name || (r.wia_only ? 'Sócio W IA' : '(sem sócio)')}
                    </div>

                    <div className="mt-1 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-0.5 text-xs text-white/50">
                      <div><span className="text-white/30">Telegram:</span> @{r.telegram_username || '—'}</div>
                      <div><span className="text-white/30">Telefone:</span> {r.phone || '—'}</div>
                      <div><span className="text-white/30">chat_id:</span> <code className="text-cyan-300/70">{r.chat_id}</code></div>
                      <div><span className="text-white/30">Vinculado:</span> {(r.linked_at || '').slice(0, 10) || '—'}</div>
                    </div>
                  </div>

                  {/* Right: Actions */}
                  <div className="flex flex-col sm:flex-row gap-2 sm:min-w-[240px]">
                    <button
                      data-testid={`bot-link-send-${r.chat_id}`}
                      onClick={() => openCompose(r)}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-cyan-500/10 hover:bg-cyan-500/20 border border-cyan-400/30 text-cyan-100 text-sm transition"
                    >
                      <Send className="w-4 h-4" />
                      Enviar
                    </button>
                    <button
                      data-testid={`bot-link-revoke-${r.chat_id}`}
                      onClick={() => revoke(r)}
                      disabled={revoking === r.chat_id}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 text-red-200 text-sm transition disabled:opacity-50 disabled:cursor-wait"
                    >
                      <Trash2 className="w-4 h-4" />
                      {revoking === r.chat_id ? 'Revogando...' : 'Revogar'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer help */}
        <p className="text-xs text-white/40 mt-6 leading-relaxed">
          <strong className="text-white/60">Como funciona:</strong> quando você revoga um vínculo, o `chat_id` do Telegram é removido do banco.
          Na próxima mensagem que o usuário mandar ao @W_ia_bot, ele será solicitado a compartilhar o contato novamente para se re-autenticar.
          Vínculos <span className="text-amber-300">Órfãos</span> são aqueles onde o sócio foi deletado E não há plano W IA ativo — seguros para revogar.
        </p>
      </div>

      {/* Compose Modal */}
      {composeTarget && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-md flex items-end sm:items-center justify-center p-4"
          onClick={closeCompose}
          data-testid="bot-link-compose-modal"
        >
          <div
            className="w-full max-w-lg rounded-3xl border border-white/10 overflow-hidden"
            style={{
              background: 'linear-gradient(180deg,rgba(20,30,45,.96) 0%,rgba(8,12,20,.98) 100%)',
              backdropFilter: 'blur(24px)',
              WebkitBackdropFilter: 'blur(24px)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="p-5 border-b border-white/5 flex items-start justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-full bg-cyan-500/15 border border-cyan-400/30 flex items-center justify-center flex-shrink-0">
                  <Send className="w-4 h-4 text-cyan-300" />
                </div>
                <div className="min-w-0">
                  <div className="text-base font-medium text-white/95 truncate">
                    {composeTarget.member_name || (composeTarget.wia_only ? 'Sócio W IA' : '(sem sócio)')}
                  </div>
                  <div className="text-xs text-white/50 truncate">
                    via @W_ia_bot · @{composeTarget.telegram_username || '—'}
                  </div>
                </div>
              </div>
              <button
                data-testid="bot-link-compose-close"
                onClick={closeCompose}
                disabled={sending}
                className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center transition disabled:opacity-40"
              >
                <XIcon className="w-4 h-4" />
              </button>
            </div>

            {/* Body */}
            <div className="p-5 space-y-3">
              <label className="text-xs uppercase tracking-wider text-white/50">
                Mensagem (chega prefixada com &quot;📢 Mensagem da administração WClub&quot;)
              </label>
              <textarea
                data-testid="bot-link-compose-textarea"
                value={composeText}
                onChange={(e) => setComposeText(e.target.value)}
                placeholder="Escreva a mensagem que será enviada via @W_ia_bot..."
                maxLength={4000}
                rows={6}
                autoFocus
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-cyan-400/50 focus:outline-none text-sm placeholder-white/30 resize-none"
              />
              <div className="flex items-center justify-between text-xs text-white/40">
                <span>Suporta *negrito*, _itálico_ e `código` (Markdown)</span>
                <span>{composeText.length} / 4000</span>
              </div>
            </div>

            {/* Footer */}
            <div className="p-5 pt-0 flex gap-2">
              <button
                onClick={closeCompose}
                disabled={sending}
                className="flex-1 px-4 py-2.5 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-sm transition disabled:opacity-40"
              >
                Cancelar
              </button>
              <button
                data-testid="bot-link-compose-send"
                onClick={sendMessage}
                disabled={sending || !composeText.trim()}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-cyan-500/15 hover:bg-cyan-500/25 border border-cyan-400/40 text-cyan-100 text-sm font-medium transition disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Send className="w-4 h-4" />
                {sending ? 'Enviando...' : 'Enviar via bot'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sent Toast */}
      {sentToast && (
        <div
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-5 py-3 rounded-full border border-emerald-400/40 bg-emerald-500/15 backdrop-blur-md text-emerald-100 text-sm flex items-center gap-2 shadow-2xl"
          data-testid="bot-link-sent-toast"
        >
          <CheckCircle2 className="w-4 h-4" />
          {sentToast}
        </div>
      )}
    </div>
  );
}
